import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import Button from '@hellodarwin/core/lib/components/common/button';
import Spin from '@hellodarwin/core/lib/components/common/spin';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Switch from 'antd/es/switch';
import { useEffect } from 'react';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import {
  GrantProfileNotificationPreference,
  GrantProfileNotificationPreferenceUpdate,
} from '../../features/api/grant-profile-types';
import {
  fetchGrantProfileNotificationPreference,
  selectGrantProfileNotificationPreferenceEntities,
  selectGrantProfileNotificationPreferenceReadErrors,
  selectGrantProfileNotificationPreferenceReadStatuses,
  selectGrantProfileNotificationPreferenceUpdateErrors,
  selectGrantProfileNotificationPreferenceUpdateStatuses,
  updateGrantProfileNotificationPreference,
} from '../../features/api/slices/grant-profile-notification-preferences-slice';
import {
  fetchGrantProfiles,
  selectAllGrantProfiles,
  selectGrantProfilesListError,
  selectGrantProfilesListLoaded,
  selectGrantProfilesListLoading,
} from '../../features/api/slices/grant-profiles-slice';
import { useNewClientApi } from '../../features/api/use-client-api';
import {
  ProfilePreferenceCard,
  ProfilePreferenceCardHeader,
  ProfilePreferenceCopy,
  ProfilePreferenceError,
  ProfilePreferenceRow,
  ProfilePreferenceRows,
  ProfilePreferencesHeader,
  ProfilePreferencesList,
  ProfilePreferencesSection,
  ProfilePreferencesState,
} from './styles';

type PreferenceKey = keyof GrantProfileNotificationPreferenceUpdate;

const preferenceRows: Array<{
  key: PreferenceKey;
  titleKey: string;
  bodyKey: string;
}> = [
  {
    key: 'grant_alerts_enabled',
    titleKey: 'alerts|profileGrantAlertsTitle',
    bodyKey: 'alerts|profileGrantAlertsBody',
  },
  {
    key: 'weekly_digest_enabled',
    titleKey: 'alerts|profileWeeklyDigestTitle',
    bodyKey: 'alerts|profileWeeklyDigestBody',
  },
  {
    key: 'monthly_digest_enabled',
    titleKey: 'alerts|profileMonthlyDigestTitle',
    bodyKey: 'alerts|profileMonthlyDigestBody',
  },
];

const preferenceUpdate = (
  preference: GrantProfileNotificationPreference,
  key: PreferenceKey,
  enabled: boolean,
): GrantProfileNotificationPreferenceUpdate => ({
  weekly_digest_enabled: preference.weekly_digest_enabled,
  monthly_digest_enabled: preference.monthly_digest_enabled,
  grant_alerts_enabled: preference.grant_alerts_enabled,
  [key]: enabled,
});

const GrantProfileNotificationPreferences = () => {
  const { accessLevel } = usePremiumAccess();
  const api = useNewClientApi();
  const dispatch = useAppDispatch();
  const { t } = useTranslations();
  const theme = useTheme();
  const profiles = useAppSelector(selectAllGrantProfiles);
  const profilesLoaded = useAppSelector(selectGrantProfilesListLoaded);
  const profilesLoading = useAppSelector(selectGrantProfilesListLoading);
  const profilesError = useAppSelector(selectGrantProfilesListError);
  const preferences = useAppSelector(
    selectGrantProfileNotificationPreferenceEntities,
  );
  const readStatuses = useAppSelector(
    selectGrantProfileNotificationPreferenceReadStatuses,
  );
  const updateStatuses = useAppSelector(
    selectGrantProfileNotificationPreferenceUpdateStatuses,
  );
  const readErrors = useAppSelector(
    selectGrantProfileNotificationPreferenceReadErrors,
  );
  const updateErrors = useAppSelector(
    selectGrantProfileNotificationPreferenceUpdateErrors,
  );
  const isExpert = accessLevel === 'expert';

  useEffect(() => {
    if (
      !isExpert ||
      profilesLoaded ||
      profilesLoading ||
      profilesError.error
    ) {
      return;
    }
    void dispatch(fetchGrantProfiles({ api }));
  }, [
    api,
    dispatch,
    isExpert,
    profilesError.error,
    profilesLoaded,
    profilesLoading,
  ]);

  useEffect(() => {
    if (!isExpert || !profilesLoaded) return;
    profiles.forEach((profile) => {
      const profileID = profile.grant_profile_id;
      const readStatus = readStatuses[profileID] ?? 'idle';
      if (!preferences[profileID] && readStatus === 'idle') {
        void dispatch(
          fetchGrantProfileNotificationPreference({
            api,
            grantProfileId: profileID,
          }),
        );
      }
    });
  }, [
    api,
    dispatch,
    isExpert,
    preferences,
    profiles,
    profilesLoaded,
    readStatuses,
  ]);

  if (!isExpert) return null;

  const retryProfiles = () => {
    void dispatch(fetchGrantProfiles({ api }));
  };

  const retryPreference = (grantProfileId: string) => {
    void dispatch(
      fetchGrantProfileNotificationPreference({ api, grantProfileId }),
    );
  };

  const updatePreference = (
    preference: GrantProfileNotificationPreference,
    key: PreferenceKey,
    enabled: boolean,
  ) => {
    void dispatch(
      updateGrantProfileNotificationPreference({
        api,
        grantProfileId: preference.grant_profile_id,
        request: preferenceUpdate(preference, key, enabled),
      }),
    );
  };

  return (
    <ProfilePreferencesSection>
      <ProfilePreferencesHeader>
        <Typography elementTheme="h6">
          {t('alerts|profilePreferencesTitle')}
        </Typography>
        <Typography elementTheme="body2" color={theme.colors.grey_2}>
          {t('alerts|profilePreferencesBody')}
        </Typography>
      </ProfilePreferencesHeader>

      {!profilesLoaded && !profilesError.error ? (
        <ProfilePreferencesState>
          <Spin />
        </ProfilePreferencesState>
      ) : profilesError.error && profiles.length === 0 ? (
        <ProfilePreferencesState>
          <Typography elementTheme="body2">
            {t('alerts|profilePreferencesLoadError')}
          </Typography>
          <Button variant="outline" size="small" onClick={retryProfiles}>
            {t('alerts|retry')}
          </Button>
        </ProfilePreferencesState>
      ) : profilesLoaded && profiles.length === 0 ? (
        <ProfilePreferencesState>
          <Typography elementTheme="body2">
            {t('alerts|profilePreferencesEmpty')}
          </Typography>
          <Button
            variant="navigation"
            size="small"
            to="/grant-profiles?create=true"
          >
            {t('alerts|profilePreferencesCreate')}
          </Button>
        </ProfilePreferencesState>
      ) : (
        <ProfilePreferencesList>
          {profiles.map((profile) => {
            const profileID = profile.grant_profile_id;
            const preference = preferences[profileID];
            const reading = readStatuses[profileID] === 'pending';
            const readFailed = Boolean(readErrors[profileID]?.error);
            const updating = updateStatuses[profileID] === 'pending';
            const updateFailed = Boolean(updateErrors[profileID]?.error);

            return (
              <ProfilePreferenceCard key={profileID}>
                <ProfilePreferenceCardHeader>
                  <Typography.Title level={3} elementTheme="h6" noMargin>
                    {profile.name}
                  </Typography.Title>
                  <Typography elementTheme="body3" color={theme.colors.grey_2}>
                    {t('alerts|profilePreferencesCardDescription')}
                  </Typography>
                </ProfilePreferenceCardHeader>

                {!preference && (reading || !readFailed) ? (
                  <ProfilePreferencesState>
                    <Spin />
                  </ProfilePreferencesState>
                ) : !preference ? (
                  <ProfilePreferencesState>
                    <Typography elementTheme="body2">
                      {t('alerts|profilePreferenceLoadError')}
                    </Typography>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => retryPreference(profileID)}
                    >
                      {t('alerts|retry')}
                    </Button>
                  </ProfilePreferencesState>
                ) : (
                  <ProfilePreferenceRows>
                    {preferenceRows.map((row) => (
                      <ProfilePreferenceRow key={row.key}>
                        <ProfilePreferenceCopy>
                          <Typography elementTheme="subtitle2">
                            {t(row.titleKey)}
                          </Typography>
                          <Typography
                            elementTheme="body2"
                            color={theme.colors.grey_2}
                          >
                            {t(row.bodyKey)}
                          </Typography>
                        </ProfilePreferenceCopy>
                        <Switch
                          aria-label={`${profile.name}: ${t(row.titleKey)}`}
                          checked={preference[row.key]}
                          disabled={updating}
                          loading={updating}
                          onChange={(enabled) =>
                            updatePreference(preference, row.key, enabled)
                          }
                        />
                      </ProfilePreferenceRow>
                    ))}
                    {updateFailed && (
                      <ProfilePreferenceError>
                        <Typography
                          elementTheme="body3"
                          color={theme.colors.error}
                        >
                          {t('alerts|profilePreferenceUpdateError')}
                        </Typography>
                      </ProfilePreferenceError>
                    )}
                  </ProfilePreferenceRows>
                )}
              </ProfilePreferenceCard>
            );
          })}
        </ProfilePreferencesList>
      )}
    </ProfilePreferencesSection>
  );
};

export default GrantProfileNotificationPreferences;
